import React, { useContext, useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import InputModern from 'components/ui/Input/InputModern';
import { Trans } from 'react-i18next';
import { CompanyContext } from 'contexts/CompanyContext';
import { CompanyOnboardingContext } from 'contexts/CompanyOnboardingContext';
import autosize from 'autosize';
import { useWhitelabel } from 'contexts/WhitelabelThemeContext';
import { TbBuildingStore } from 'react-icons/tb';
import useDarkMode from 'utils/hooks/useDarkMode';

export default function StepCompanyProfile() {
  const { company } = useContext(CompanyContext);
  const { setCurrentStepData } = useContext(CompanyOnboardingContext);
  const { whitelabel } = useWhitelabel();
  const [isDarkMode] = useDarkMode();
  const [name, setName] = useState(company?.name || '');
  const [description, setDescription] = useState(company?.description || '');
  const descriptionInputRef = useRef(null);
  const editedRef = useRef(false);

  // The previous step saves the company through an invalidation that resolves after
  // updateCompany does, so on mount `company` can still hold the pre-save values.
  // useState only reads them on the first render, which left both fields permanently
  // empty once the website step had filled them in. Hydrate when the refreshed company
  // arrives, but never over anything the user has typed.
  useEffect(() => {
    if (editedRef.current) return;
    if (company?.name) setName(company.name);
    if (company?.description) setDescription(company.description);
  }, [company?.name, company?.description]);

  // Update the context with current data
  useEffect(() => {
    setCurrentStepData({ name, description });
  }, [name, description, setCurrentStepData]);

  useEffect(() => {
    if (descriptionInputRef.current) {
      autosize(descriptionInputRef.current);
    }
  }, [descriptionInputRef.current]);

  useEffect(() => {
    if (description) {
      autosize(descriptionInputRef.current);
    }
  }, [description]);

  const charCount = description?.trim()?.length || 0;
  const charLimit = 3000;
  const charPercentage = (charCount / charLimit) * 100;

  return (
    <>
      <div className="flex flex-col gap-4 md:gap-6 flex-1 overflow-y-auto">
        {/* Animated Icon */}
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: "spring", stiffness: 200, damping: 20 }}
          className="flex justify-center mb-2 md:mb-4"
        >
          <div className="w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 rounded-2xl md:rounded-3xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-2xl shadow-emerald-500/50">
            <TbBuildingStore className="text-3xl md:text-4xl lg:text-5xl text-white" />
          </div>
        </motion.div>

        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-center"
        >
          <h3 className={`text-2xl md:text-3xl lg:text-4xl font-black mb-2 md:mb-3 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            Tell us about your company
          </h3>
          <p className={`text-base md:text-lg ${
            isDarkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Help our AI understand your business better
          </p>
        </motion.div>

        {/* Company Name Input */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-2 md:space-y-3"
        >
          <label className={`block text-xs md:text-sm font-bold ${
            isDarkMode ? 'text-gray-300' : 'text-gray-700'
          }`}>
            Company Name
          </label>
          <InputModern 
            name="name"
            placeholder="Acme Inc."
            className={`!w-full !px-4 !py-3 md:!px-6 md:!py-4 text-base md:text-lg !rounded-xl md:!rounded-2xl !border-2 focus:!border-purple-500 transition-all ${
              isDarkMode
                ? '!bg-white/5 !border-white/10 !text-white placeholder:!text-gray-500'
                : '!bg-gray-50 !border-gray-200 !text-gray-900 placeholder:!text-gray-400'
            }`}
            autoFocus={true}
            value={name}
            onChange={(e) => {
              editedRef.current = true;
              setName(e.target.value);
            }}
          />
        </motion.div>

        {/* Company Description */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="space-y-2 md:space-y-3 flex-1"
        >
          <div className="flex justify-between items-center">
            <label className={`block text-xs md:text-sm font-bold ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Company Description
            </label>
            <div className={`text-[10px] md:text-xs font-mono ${
              charPercentage > 90 
                ? 'text-orange-500' 
                : isDarkMode ? 'text-gray-500' : 'text-gray-400'
            }`}>
              {charCount} / {charLimit}
            </div>
          </div>
          <div className='relative'>
            <InputModern 
              ref={descriptionInputRef}
              name="description"
              placeholder="We are a technology company that..."
              className={`!w-full !px-4 !py-3 md:!px-6 md:!py-4 text-sm md:text-base !rounded-xl md:!rounded-2xl !border-2 focus:!border-purple-500 transition-all min-h-[150px] md:min-h-[200px] ${
                isDarkMode
                  ? '!bg-white/5 !border-white/10 !text-white placeholder:!text-gray-500'
                  : '!bg-gray-50 !border-gray-200 !text-gray-900 placeholder:!text-gray-400'
              }`}
              textArea
              value={description}
              onChange={(e) => {
                editedRef.current = true;
                setDescription(e.target.value);
              }}
            />
            {/* Character Progress Bar */}
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: charPercentage / 100 }}
              transition={{ duration: 0.3 }}
              className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-b-xl md:rounded-b-2xl origin-left"
              style={{ width: '100%' }}
            />
          </div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className={`text-xs md:text-sm ${
              isDarkMode ? 'text-gray-500' : 'text-gray-500'
            }`}
          >
            💡 The more detailed your description, the better {whitelabel?.name}'s AI can generate relevant content
          </motion.p>
        </motion.div>

        {/* Quick Tips */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className={`p-3 md:p-4 rounded-xl md:rounded-2xl ${
            isDarkMode 
              ? 'bg-emerald-500/10 border border-emerald-500/20' 
              : 'bg-emerald-50 border border-emerald-200'
          }`}
        >
          <p className={`text-[10px] md:text-xs font-bold mb-1.5 md:mb-2 ${
            isDarkMode ? 'text-emerald-300' : 'text-emerald-700'
          }`}>
            ✨ Quick Tips:
          </p>
          <ul className={`text-[10px] md:text-xs space-y-0.5 md:space-y-1 ${
            isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
          }`}>
            <li>• What products/services do you offer?</li>
            <li>• Who are your target customers?</li>
            <li>• What makes you unique?</li>
          </ul>
        </motion.div>
      </div>
    </>
  );
}
